// The public contract for third-party Dunea plugins and themes.
//
// A third-party PLUGIN is a manifest plus the developer's own HTTPS service.
// Dunea never runs a third-party plugin's backend code. It talks to it:
//
//   Dunea ──signed webhooks──▶ your service        (events you subscribe to)
//   your service ──install token──▶ Plugin API v1  (only the permissions the owner granted)
//   Dunea AI ──signed tool calls──▶ your service   (agentTools: the AI can use your plugin)
//   workbench ──sandboxed iframe──▶ your panel URL (panel)
//   published site ──<script src>──▶ your CDN       (siteScripts, after review + visitor consent)
//
// Plus the five building blocks (./primitives): custom data, blocks, checkout
// functions, admin extensions and automations.
//
// A THEME is design tokens, fonts and a style guide the Dunea AI follows, plus
// optional owner-editable sections (blocks) and page presets. Block code runs
// on the owner's site under an import allowlist and is always human-reviewed.
//
// This file is the single source of truth: Dunea's marketplace validates every
// submitted manifest with these exact schemas.

import { z } from 'zod';
import {
  ADMIN_TARGETS,
  FUNCTION_TARGETS,
  PAGE_TYPES,
  adminExtensionSchema,
  automationDeclarationsSchema,
  blockInstanceSchema,
  blockPackageSchema,
  checkBlockSchemas,
  dataDeclarationsSchema,
  functionDeclarationSchema,
  type BlockInstance,
} from './primitives';

export const MANIFEST_SCHEMA_VERSION = 1;

/** What an install may do. The owner sees and approves this list at install. */
export const PERMISSIONS = {
  'app:read': 'See the app name, published URL and whether payments are live',
  'shop:read': 'See products, variants and stock',
  'shop:write': 'Create and edit products, variants and stock',
  'orders:read': 'See orders (items, totals, status, shipping address)',
  'orders:write': 'Mark orders fulfilled and add tracking',
  'customers:read': 'See buyer names and emails on orders and leads',
  'payments:read': 'See payments and refunds (amounts, status, campaign attribution)',
  'forms:read': 'Receive form submissions',
  'reviews:read': 'See product reviews',
  'reviews:write': 'Approve, reject and reply to reviews',
  'site:scripts': 'Add scripts to the published site (shown to visitors only after consent)',
  'data:read': 'Read custom fields and custom content (metafields, metaobjects)',
  'data:write': "Write custom fields and content in this plugin's own namespace",
  'site:blocks': 'Add sections and blocks the owner can place on the site',
  'checkout:functions': 'Run discount, shipping, payment-method, validation or tax logic at checkout',
  'automations:extend': 'Add triggers and actions to the owner’s automations',
} as const;

export type Permission = keyof typeof PERMISSIONS;

export const PERMISSION_IDS = Object.keys(PERMISSIONS) as Permission[];

/** Events a third-party plugin can subscribe to, and the permissions each needs. */
export const PUBLIC_EVENTS = {
  'plugin.installed': [],
  'plugin.uninstalled': [],
  'plugin.settings.updated': [],
  'plugin.reactivated': [],
  'app.published': ['app:read'],
  'shop.order.paid': ['orders:read'],
  'shop.order.fulfilled': ['orders:read'],
  'shop.order.refunded': ['orders:read'],
  'shop.stock.low': ['shop:read'],
  'payments.payment.succeeded': ['payments:read'],
  'payments.payment.refunded': ['payments:read'],
  'forms.submitted': ['forms:read', 'customers:read'],
  'funnels.lead.captured': ['customers:read'],
  'reviews.submitted': ['reviews:read'],
} as const satisfies Record<string, readonly Permission[]>;

export type PublicEvent = keyof typeof PUBLIC_EVENTS;

export const PUBLIC_EVENT_IDS = Object.keys(PUBLIC_EVENTS) as PublicEvent[];

export const PLUGIN_CATEGORIES = [
  'marketing',
  'sales',
  'shipping',
  'payments',
  'analytics',
  'customer-support',
  'content',
  'design',
  'productivity',
  'ai',
  'integrations',
] as const;

export const THEME_CATEGORIES = [
  'minimal',
  'bold',
  'editorial',
  'playful',
  'luxury',
  'tech',
  'retail',
  'portfolio',
  'local-business',
] as const;

/** What a template site is for (the businesses Dunea users actually run). */
export const TEMPLATE_CATEGORIES = [
  'restaurant',
  'retail',
  'salon-beauty',
  'health',
  'education',
  'professional-services',
  'trades',
  'events',
  'portfolio',
  'agency',
  'community',
  'saas',
] as const;

/** What an AI skill pack teaches the builder. */
export const SKILL_CATEGORIES = [
  'copywriting',
  'seo',
  'design',
  'ecommerce',
  'marketing',
  'industry',
  'localization',
  'accessibility',
  'conversion',
] as const;

/** Listing kinds. Plugins run on the developer's service; the other three are data only. */
export const LISTING_KINDS = ['plugin', 'theme', 'template', 'skill'] as const;
export type ListingKindId = (typeof LISTING_KINDS)[number];

/** Google Fonts families themes may use (loaded from fonts.googleapis.com). */
export const THEME_FONTS = [
  'Inter',
  'Geist',
  'DM Sans',
  'Manrope',
  'Plus Jakarta Sans',
  'Space Grotesk',
  'Outfit',
  'Sora',
  'Work Sans',
  'Poppins',
  'Montserrat',
  'Raleway',
  'Nunito',
  'Lato',
  'Source Sans 3',
  'IBM Plex Sans',
  'Instrument Serif',
  'Playfair Display',
  'Cormorant Garamond',
  'Fraunces',
  'DM Serif Display',
  'Libre Baskerville',
  'Lora',
  'Merriweather',
  'EB Garamond',
  'Syne',
  'Bricolage Grotesque',
  'JetBrains Mono',
  'Space Mono',
  'Geist Mono',
] as const;

const id = z
  .string()
  .regex(/^[a-z][a-z0-9-]{2,40}$/, 'lowercase letters, digits and dashes, 3–41 chars, starting with a letter');
const semver = z.string().regex(/^\d+\.\d+\.\d+$/, 'semver x.y.z');
const httpsUrl = z
  .string()
  .url()
  .max(500)
  .refine((u) => u.startsWith('https://'), 'must be https://');
const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'a #rrggbb colour');
/** A credit amount: 0.05–50, at most two decimals. One credit is CREDIT_VALUE_USD to the owner. */
const credits = z
  .number()
  .min(0.05)
  .max(50)
  .refine((n) => Math.abs(Math.round(n * 100) - n * 100) < 1e-9, 'at most two decimals');
const listingSlug = z.string().regex(/^[a-z][a-z0-9-]{2,40}$/);

export const pricingSchema = z.discriminatedUnion('model', [
  z.object({ model: z.literal('free') }),
  z.object({
    model: z.literal('one_time'),
    /** USD cents, $1–$999. */
    amount: z.number().int().min(100).max(99_900),
    currency: z.literal('USD'),
  }),
  z.object({
    model: z.literal('monthly'),
    amount: z.number().int().min(100).max(99_900),
    currency: z.literal('USD'),
    trialDays: z.number().int().min(0).max(30).optional(),
  }),
]);

export type Pricing = z.infer<typeof pricingSchema>;

const developerSchema = z.object({
  name: z.string().min(2).max(80),
  website: httpsUrl.optional(),
  supportEmail: z.string().email(),
  privacyUrl: httpsUrl,
});

const listingSchema = {
  schemaVersion: z.literal(MANIFEST_SCHEMA_VERSION),
  id,
  version: semver,
  name: z.string().min(2).max(60),
  tagline: z.string().min(10).max(120),
  description: z.string().min(40).max(4_000),
  icon: httpsUrl,
  screenshots: z.array(httpsUrl).max(8).default([]),
  developer: developerSchema,
  pricing: pricingSchema,
};

const settingsFieldSchema = z.object({
  key: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,40}$/),
  label: z.string().min(1).max(80),
  type: z.enum(['text', 'secret', 'select', 'boolean', 'number', 'email', 'url']),
  help: z.string().max(300).optional(),
  placeholder: z.string().max(120).optional(),
  required: z.boolean().optional(),
  options: z
    .array(z.object({ value: z.string().max(80), label: z.string().max(80) }))
    .max(50)
    .optional(),
});

/** JSON-schema-ish input definition for an AI tool (object with typed properties). */
const toolInputSchema = z.object({
  type: z.literal('object'),
  properties: z.record(
    z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,40}$/),
    z.object({
      type: z.enum(['string', 'number', 'integer', 'boolean']),
      description: z.string().max(300),
      enum: z.array(z.string().max(80)).max(30).optional(),
    }),
  ),
  required: z.array(z.string()).max(20).optional(),
});

export const pluginManifestSchema = z
  .object({
    ...listingSchema,
    kind: z.literal('plugin'),
    categories: z.array(z.enum(PLUGIN_CATEGORIES)).min(1).max(3),
    permissions: z.array(z.enum(PERMISSION_IDS as [Permission, ...Permission[]])).max(PERMISSION_IDS.length),
    subscribes: z
      .array(z.enum(PUBLIC_EVENT_IDS as [PublicEvent, ...PublicEvent[]]))
      .max(PUBLIC_EVENT_IDS.length)
      .default([]),
    /** Where Dunea POSTs signed events and tool calls. Required if you subscribe to events or define tools. */
    webhookUrl: httpsUrl.optional(),
    /** Workbench panel, rendered in a sandboxed iframe with a short-lived session token. */
    panel: z.object({ url: httpsUrl, title: z.string().min(2).max(40) }).optional(),
    /** Tools the Dunea AI can call while building or managing the owner's app. */
    agentTools: z
      .array(
        z.object({
          name: z.string().regex(/^[a-z][a-z0-9_]{2,40}$/),
          description: z.string().min(20).max(600),
          input: toolInputSchema,
          /**
           * Optional price per SUCCESSFUL call (your service answered 2xx with
           * ok: true), in Dunea credits, charged to the app owner's credits
           * within the monthly cap they approved. The AI is told the price.
           */
          price: z.object({ credits }).optional(),
        }),
      )
      .max(10)
      .default([]),
    /**
     * Optional metered usage your SERVICE reports through the Plugin API
     * (POST /usage), e.g. "per label printed". Charged to the owner's credits
     * within their monthly cap; you earn the developer share.
     */
    usage: z
      .object({
        unit: z.string().min(2).max(40),
        /** Most you may charge for one unit. */
        maxCreditsPerUnit: credits,
        description: z.string().min(20).max(300),
        /** The monthly cap Dunea suggests to the owner at install (they can change it). */
        suggestedMonthlyCapCredits: z.number().min(1).max(10_000).default(50),
      })
      .optional(),
    /** Scripts on the published site. Needs `site:scripts`; reviewed by Dunea. */
    siteScripts: z
      .array(
        z.object({
          src: httpsUrl,
          consent: z.enum(['necessary', 'analytics', 'marketing']),
          placement: z.enum(['head', 'body']).default('body'),
        }),
      )
      .max(3)
      .default([]),
    /** Settings the owner fills in; Dunea renders the form and sends values to you. */
    settings: z.array(settingsFieldSchema).max(20).default([]),
    /** Custom data definitions created in your namespace at install (primitives/data). */
    data: dataDeclarationsSchema.optional(),
    /** Sections, blocks and app embeds the owner can place (primitives/blocks). Needs `site:blocks`. */
    blocks: z.array(blockPackageSchema).max(20).default([]),
    /** Checkout functions (primitives/functions). Needs `checkout:functions`. */
    functions: z.array(functionDeclarationSchema).max(10).default([]),
    /** Cards and actions inside Dunea's panels (primitives/admin). */
    adminExtensions: z.array(adminExtensionSchema).max(10).default([]),
    /** Automation triggers and actions (primitives/automations). Needs `automations:extend`. */
    automation: automationDeclarationsSchema,
  })
  .superRefine((m, ctx) => {
    const need = (permission: Permission, path: string, what: string) => {
      if (!m.permissions.includes(permission)) {
        ctx.addIssue({ code: 'custom', path: [path], message: `${what} need the "${permission}" permission` });
      }
    };

    if (m.data && (m.data.metafields.length || m.data.metaobjects.length)) {
      need('data:write', 'data', 'data definitions');
    }

    if (m.blocks.length) {
      need('site:blocks', 'blocks', 'blocks');
      checkBlockSchemas(m.blocks, (message) => ctx.addIssue({ code: 'custom', path: ['blocks'], message }));
    }

    for (const f of m.functions) {
      need('checkout:functions', 'functions', 'functions');
      need(FUNCTION_TARGETS[f.target].permission, 'functions', `"${f.target}" functions`);
    }

    for (const x of m.adminExtensions) {
      need(ADMIN_TARGETS[x.target].permission, 'adminExtensions', `"${x.target}" extensions`);
    }

    if (m.automation.triggers.length || m.automation.actions.length) {
      need('automations:extend', 'automation', 'automation triggers and actions');
    }

    if (m.automation.actions.length && !m.webhookUrl) {
      ctx.addIssue({ code: 'custom', path: ['webhookUrl'], message: 'webhookUrl is required for automation actions' });
    }

    for (const event of m.subscribes) {
      for (const needed of PUBLIC_EVENTS[event]) {
        if (!m.permissions.includes(needed)) {
          ctx.addIssue({
            code: 'custom',
            path: ['subscribes'],
            message: `"${event}" needs the "${needed}" permission`,
          });
        }
      }
    }

    if ((m.subscribes.length > 0 || m.agentTools.length > 0) && !m.webhookUrl) {
      ctx.addIssue({
        code: 'custom',
        path: ['webhookUrl'],
        message: 'webhookUrl is required for events and agent tools',
      });
    }

    if (m.siteScripts.length > 0 && !m.permissions.includes('site:scripts')) {
      ctx.addIssue({
        code: 'custom',
        path: ['siteScripts'],
        message: 'siteScripts need the "site:scripts" permission',
      });
    }

    const names = new Set<string>();

    for (const t of m.agentTools) {
      if (names.has(t.name)) {
        ctx.addIssue({ code: 'custom', path: ['agentTools'], message: `duplicate tool "${t.name}"` });
      }

      names.add(t.name);

      for (const r of t.input.required ?? []) {
        if (!(r in t.input.properties)) {
          ctx.addIssue({
            code: 'custom',
            path: ['agentTools'],
            message: `tool "${t.name}" requires unknown field "${r}"`,
          });
        }
      }
    }
  });

export type PluginManifest = z.infer<typeof pluginManifestSchema>;
export type PluginManifestInput = z.input<typeof pluginManifestSchema>;

const fontRole = z.enum(THEME_FONTS as unknown as [string, ...string[]]);

export const themeTokensSchema = z.object({
  colors: z.object({
    background: hex,
    surface: hex,
    text: hex,
    muted: hex,
    primary: hex,
    primaryText: hex,
    accent: hex,
    border: hex,
  }),
  /** Optional dark palette; omit for single-mode themes. */
  darkColors: z
    .object({
      background: hex,
      surface: hex,
      text: hex,
      muted: hex,
      primary: hex,
      primaryText: hex,
      accent: hex,
      border: hex,
    })
    .optional(),
  fonts: z.object({ heading: fontRole, body: fontRole, mono: fontRole.optional() }),
  /** Corner radius in px (0 = sharp). */
  radius: z.number().int().min(0).max(32),
  /** Base spacing density. */
  density: z.enum(['compact', 'comfortable', 'airy']),
  /** Heading scale ratio, e.g. 1.25 (major third). */
  typeScale: z.number().min(1.1).max(1.6),
});

export type ThemeTokens = z.infer<typeof themeTokensSchema>;

export const themeManifestSchema = z
  .object({
    ...listingSchema,
    kind: z.literal('theme'),
    categories: z.array(z.enum(THEME_CATEGORIES)).min(1).max(3),
    tokens: themeTokensSchema,
    /**
     * Art direction for the Dunea AI, in markdown: mood, layout habits, imagery,
     * motion, copy tone, do's and don'ts. This is what makes a theme feel like a
     * designer's, not just a palette. 200–12,000 chars.
     */
    guide: z.string().min(200).max(12_000),
    /** Optional section recipes the AI can reuse (hero, pricing, gallery…), markdown each. */
    sections: z
      .array(z.object({ name: z.string().min(2).max(60), recipe: z.string().min(40).max(3_000) }))
      .max(20)
      .default([]),
    /**
     * Real, owner-editable sections and blocks (primitives/blocks), written to
     * frontend/src/blocks/theme/ when the theme is applied. Always human-reviewed.
     */
    blocks: z.array(blockPackageSchema).max(30).default([]),
    /** Page layouts the theme suggests: a section group per page type, built from its blocks. */
    presets: z
      .array(
        z.object({
          group: z.string().regex(/^[a-zA-Z0-9_-]{1,60}$/),
          pageType: z.enum(PAGE_TYPES),
          sections: z.array(blockInstanceSchema).max(40),
        }),
      )
      .max(12)
      .default([]),
    /** Custom content types the theme's sections read (e.g. "testimonial"). Created in `custom` on apply. */
    data: dataDeclarationsSchema.optional(),
  })
  .superRefine((t, ctx) => {
    checkBlockSchemas(t.blocks, (message) => ctx.addIssue({ code: 'custom', path: ['blocks'], message }));

    const names = new Set(t.blocks.map((b) => `theme/${b.name}`));

    for (const p of t.presets) {
      const walk = (list: BlockInstance[]) => {
        for (const s of list) {
          if (!names.has(s.type)) {
            ctx.addIssue({
              code: 'custom',
              path: ['presets'],
              message: `preset "${p.group}" uses unknown block "${s.type}"`,
            });
          }

          walk(s.blocks ?? []);
        }
      };

      walk(p.sections);
    }
  });

export type ThemeManifest = z.infer<typeof themeManifestSchema>;
export type ThemeManifestInput = z.input<typeof themeManifestSchema>;

/**
 * A TEMPLATE is a whole Dunea site other people start from ("remix"). The
 * source is a project in the developer's own Dunea account; Dunea snapshots its
 * files at submission. Only code and content are copied: never environment
 * variables, secrets, plugin installs, chats, customers or orders.
 */
export const templateManifestSchema = z.object({
  ...listingSchema,
  kind: z.literal('template'),
  categories: z.array(z.enum(TEMPLATE_CATEGORIES)).min(1).max(3),
  /** The Dunea project to snapshot (must be yours). */
  sourceProjectId: z.string().uuid(),
  /** A live demo of the template (its published Dunea URL). */
  previewUrl: httpsUrl,
  /** Page names, for the listing ("Home", "Menu", "Book a table"). */
  pages: z.array(z.string().min(1).max(40)).min(1).max(30),
  /** Optional theme listing it's designed with. */
  theme: listingSlug.optional(),
  /** Plugins that make it work fully (suggested to the owner after remix, never auto-installed). */
  recommendedPlugins: z.array(listingSlug).max(10).default([]),
  /** What the owner should tell the AI first ("swap in your menu and opening hours"). */
  firstPrompt: z.string().min(10).max(300).optional(),
});

export type TemplateManifest = z.infer<typeof templateManifestSchema>;
export type TemplateManifestInput = z.input<typeof templateManifestSchema>;

/**
 * A SKILL is know-how the Dunea AI loads while it builds an installed app:
 * markdown instructions (e.g. "UK restaurant compliance and menus", "Arabic
 * RTL storefronts"). Data only, like themes. Installed per app.
 */
export const skillManifestSchema = z.object({
  ...listingSchema,
  kind: z.literal('skill'),
  categories: z.array(z.enum(SKILL_CATEGORIES)).min(1).max(3),
  /** The instructions the AI follows, in markdown. 200–20,000 chars. */
  instructions: z.string().min(200).max(20_000),
  /** When the AI should apply it (short phrases, e.g. "menu page", "halal certification"). */
  triggers: z.array(z.string().min(3).max(80)).min(1).max(12),
  recommendedThemes: z.array(listingSlug).max(5).default([]),
  recommendedPlugins: z.array(listingSlug).max(5).default([]),
});

export type SkillManifest = z.infer<typeof skillManifestSchema>;
export type SkillManifestInput = z.input<typeof skillManifestSchema>;

export type AnyManifest = PluginManifest | ThemeManifest | TemplateManifest | SkillManifest;

export interface ValidationResult<T> {
  ok: boolean;
  manifest?: T;
  errors: string[];
}

function format(error: z.ZodError): string[] {
  return error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`);
}

/** Validate a plugin or theme manifest exactly as the marketplace does. */
export function validateManifest(input: unknown): ValidationResult<AnyManifest> {
  const kind = (input as { kind?: unknown } | null)?.kind;
  const schemas = {
    plugin: pluginManifestSchema,
    theme: themeManifestSchema,
    template: templateManifestSchema,
    skill: skillManifestSchema,
  } as const;
  const schema = typeof kind === 'string' && kind in schemas ? schemas[kind as ListingKindId] : null;

  if (!schema) {
    return { ok: false, errors: ['kind: must be "plugin", "theme", "template" or "skill"'] };
  }

  const parsed = schema.safeParse(input);

  return parsed.success ? { ok: true, manifest: parsed.data, errors: [] } : { ok: false, errors: format(parsed.error) };
}

/** Typed helper for authoring a plugin manifest (`dunea.plugin.ts`). */
export function definePlugin(manifest: Omit<PluginManifestInput, 'schemaVersion' | 'kind'>): PluginManifestInput {
  return { schemaVersion: MANIFEST_SCHEMA_VERSION, kind: 'plugin', ...manifest };
}

/** Typed helper for authoring a theme manifest (`dunea.theme.ts`). */
export function defineTheme(manifest: Omit<ThemeManifestInput, 'schemaVersion' | 'kind'>): ThemeManifestInput {
  return { schemaVersion: MANIFEST_SCHEMA_VERSION, kind: 'theme', ...manifest };
}

/** Typed helper for authoring a template manifest (`dunea.template.ts`). */
export function defineTemplate(manifest: Omit<TemplateManifestInput, 'schemaVersion' | 'kind'>): TemplateManifestInput {
  return { schemaVersion: MANIFEST_SCHEMA_VERSION, kind: 'template', ...manifest };
}

/** Typed helper for authoring a skill manifest (`dunea.skill.ts`). */
export function defineSkill(manifest: Omit<SkillManifestInput, 'schemaVersion' | 'kind'>): SkillManifestInput {
  return { schemaVersion: MANIFEST_SCHEMA_VERSION, kind: 'skill', ...manifest };
}

// ─── How creators earn ───────────────────────────────────────────────────────
//
// Six streams, one ledger, one payout account (Stripe Connect Express):
//
//   sale          one-time price on a plugin, theme, template or skill
//   subscription  monthly price on any listing
//   usage         paid AI tool calls + metered usage, paid from owners' credits
//   referral      a share of what customers you bring to Dunea spend (12 months)
//   services      client work found through the Experts directory (lead → hand-off)
//
// Founding creator terms: every creator keeps 100% of their first
// FEE_SCHEDULE.founderAllowanceCents of lifetime gross. After that Dunea takes
// feeBps for the stream. Terms only ever move in creators' favour.

export const EARNING_STREAMS = ['sale', 'subscription', 'usage', 'referral', 'services'] as const;
export type EarningStream = (typeof EARNING_STREAMS)[number];

export const FEE_SCHEDULE = {
  /** Lifetime gross (USD cents) each creator keeps 100% of. */
  founderAllowanceCents: 2_500_000,
  /** Dunea's fee after the allowance, in basis points, by listing kind. Usage is charged like plugins. */
  feeBpsByKind: { plugin: 1_500, skill: 1_500, theme: 1_000, template: 1_000 } as Record<ListingKindId, number>,
  usageFeeBps: 1_500,
  /** Creator / expert referrals: share of a referred customer's Dunea spend, and for how long. */
  referralShareBps: 2_500,
  referralMonths: 12,
  /** Usage and referral earnings become payable after this hold (refunds and chargebacks settle first). */
  payoutHoldDays: 30,
  /** Smallest transfer for usage + referral earnings. */
  minimumPayoutCents: 2_000,
} as const;

/**
 * Dunea's fee on one charge, given the creator's lifetime gross BEFORE it.
 * Only the part of the charge above the founder allowance is fee-bearing, and
 * the fee rounds down (so the creator's share is a floor).
 */
export function platformFeeCents(amountCents: number, feeBps: number, lifetimeGrossBeforeCents: number): number {
  if (!Number.isInteger(amountCents) || amountCents < 0) {
    throw new Error(`amountCents must be a non-negative integer, got ${amountCents}`);
  }

  const allowanceLeft = Math.max(0, FEE_SCHEDULE.founderAllowanceCents - Math.max(0, lifetimeGrossBeforeCents));
  const feeBearing = Math.max(0, amountCents - allowanceLeft);

  return Math.floor((feeBearing * feeBps) / 10_000);
}

// ─── Review checklist ────────────────────────────────────────────────────────
//
// Public, stable rule ids. `dunea-plugin check` reports them, the server's
// submission checks store them, and reviewers cite them when rejecting, so a
// rejection always says exactly what to fix.

export const REVIEW_RULES = {
  'DUN-M001': 'Manifest must validate against the schema',
  'DUN-M002': 'Version must be higher than the last approved version',
  'DUN-U001': 'Endpoints must be public https URLs (no localhost, private or IP-literal hosts)',
  'DUN-U002': 'webhookUrl must respond (any status below 500)',
  'DUN-U003': 'panel.url and icon must respond 2xx/3xx',
  'DUN-L001': 'Listing needs at least 1 screenshot (3 recommended)',
  'DUN-L002': 'Description must explain what it does, who it is for and what it costs, in plain language',
  'DUN-L003': 'No misleading names, trademarks you do not own, or claims you cannot back up',
  'DUN-P001': 'Ask only for the permissions the listing actually uses',
  'DUN-P002': 'New permissions in an update are re-approved by every owner',
  'DUN-B001': 'Paid listings, paid tools and usage need Stripe payouts connected',
  'DUN-B002': 'Tool prices must be disclosed in the description and match real value',
  'DUN-S001': 'Site scripts must be necessary for the feature, consent-categorised correctly, and fast',
  'DUN-S002': 'No tracking or data collection beyond what the privacy policy states',
  'DUN-T001': 'Theme text must meet WCAG AA contrast (4.5:1) on background and surface',
  'DUN-T002': 'Primary button text must meet 4.5:1 on the primary colour',
  'DUN-T003': 'Template preview must be live and match the snapshot',
  'DUN-T004': 'Templates must not contain secrets, real customer data or another creator’s work',
  'DUN-K001': 'Skills must not instruct the AI to bypass safety, pricing or permission rules',
  'DUN-Q001': 'It has to work: reviewers install it on a test app and use the main flow',
  // Block code (primitives/blocks BLOCK_CODE_RULES): runs on the owner's site.
  'DUN-B100': 'Block code may only import react, @dunea/blocks, lucide-react and clsx',
  'DUN-B101': 'Block code must not use eval or new Function',
  'DUN-B102': 'Block code must not use dynamic import()',
  'DUN-B103': 'Block code must not make network requests; read data through @dunea/blocks hooks',
  'DUN-B104': 'Block code must not inject raw HTML; use <RichText>',
  'DUN-B105': 'Block code must not read or write cookies or browser storage',
  'DUN-B106': 'Block code must not navigate the page itself; use <Link>',
  'DUN-B107': 'Block code must not render script or iframe elements',
  'DUN-B108': 'Block code must not use Node globals (process, require, __dirname)',
  'DUN-B109': 'Block code must default-export its component',
} as const;

export type ReviewRuleId = keyof typeof REVIEW_RULES;
